import { useQuery } from '@apollo/client';

import BrowseRelevantSkillsByDomain from 'bundles/browse-common/queries/BrowseRelevantSkillsByDomain.graphql';
import BrowseSkillNamesByIds from 'bundles/browse-common/queries/BrowseSkillNamesByIds.graphql';
import type {
  BrowseRelevantSkillsByDomainQuery,
  BrowseRelevantSkillsByDomainQueryVariables,
} from 'bundles/browse-common/queries/__generated__/BrowseRelevantSkillsByDomain';
import type {
  BrowseSkillNamesByIdsQuery,
  BrowseSkillNamesByIdsQueryVariables,
} from 'bundles/browse-common/queries/__generated__/BrowseSkillNamesByIds';

/**
 * Hook to fetch related skills for given domain IDs
 * @param domainIds - Array of domain slugs to fetch skills for
 * @returns Object containing skills array and loading state
 */
function useRelatedSkills(domainIds: string[]) {
  // Remove this once we have a proper solution for health domain. Nostos is using life-sciences right now
  const domainIdsMapHealthToLifeSciences = domainIds.map((domainId) => {
    if (domainId === 'health') {
      return 'life-sciences';
    }
    return domainId;
  });

  const { data: skillIdsData, loading: skillIdsLoading } = useQuery<
    BrowseRelevantSkillsByDomainQuery,
    BrowseRelevantSkillsByDomainQueryVariables
  >(BrowseRelevantSkillsByDomain, {
    variables: {
      jobName: 'browse_relevant_skills_by_domain',
      domainIds: JSON.stringify(domainIdsMapHealthToLifeSciences),
    },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: domainIds.length === 0,
  });

  const skillIdsString =
    skillIdsData?.NaptimeQueries.ExternallyAccessibleNostosV1Resource.getAllProperties?.elements[0]?.content.skills;
  const skillIds: string[] = skillIdsString ? JSON.parse(skillIdsString) : [];

  const { data: skillNamesData, loading: skillNamesLoading } = useQuery<
    BrowseSkillNamesByIdsQuery,
    BrowseSkillNamesByIdsQueryVariables
  >(BrowseSkillNamesByIds, {
    variables: { skillIds },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: skillIds.length === 0,
  });

  const skills = skillNamesData?.Skills?.multiGetSkillsById || [];
  return { skills, loading: skillIdsLoading || skillNamesLoading };
}

export default useRelatedSkills;
